import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconSearch } from '@tabler/icons';
import { extractPlaygroundError } from '../../../api/integrations-playground-service';
import {
  usePlaygroundItems,
  usePlaygroundObjects,
} from '../../../queries/use-integrations-playground';
import { PlaygroundObjectSelect } from './playground-object-select';
import { PlaygroundErrorDisplay } from './playground-error-display';
import { JsonBlock } from './json-block';

const MAX_IDS = 50;
const MAX_ITEMS = 200;
const MAX_RANGE_DAYS = 31;
const DAY_MS = 24 * 60 * 60 * 1000;

type ItemsMode = 'ids' | 'dateRange';
type DateField = 'createdAt' | 'updatedAt';

interface PlaygroundItemsPanelProps {
  accountId: string;
  integrationKey: string;
}

function parseIds(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}

export function PlaygroundItemsPanel({ accountId, integrationKey }: PlaygroundItemsPanelProps) {
  const [objectType, setObjectType] = useState<string | null>(null);
  const [mode, setMode] = useState<ItemsMode>('dateRange');
  const [idsText, setIdsText] = useState('');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [dateField, setDateField] = useState<DateField>('updatedAt');
  const [maxItems, setMaxItems] = useState<number>(50);
  const [extraParamsText, setExtraParamsText] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const { data: objectsData } = usePlaygroundObjects(accountId, integrationKey);
  const selectedObject = useMemo(
    () => objectsData?.items.find((object) => object.name === objectType),
    [objectsData, objectType],
  );

  const mutation = usePlaygroundItems();
  const ids = useMemo(() => parseIds(idsText), [idsText]);

  const validate = (): string | null => {
    if (!objectType) return 'Select an object type.';
    if (mode === 'ids') {
      if (ids.length === 0) return 'Enter at least one id.';
      if (ids.length > MAX_IDS) return `Too many ids (${ids.length}). Max ${MAX_IDS}.`;
      return null;
    }
    if (!fromDate || !toDate) return 'Pick both a from and to date.';
    if (fromDate >= toDate) return 'From date must be before to date.';
    if (toDate.getTime() - fromDate.getTime() > MAX_RANGE_DAYS * DAY_MS) {
      return `Date range exceeds ${MAX_RANGE_DAYS} days.`;
    }
    if (extraParamsText.trim()) {
      try {
        const parsed = JSON.parse(extraParamsText);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          return 'extraParams must be a JSON object.';
        }
      } catch {
        return 'extraParams must be valid JSON.';
      }
    }
    return null;
  };

  const handleRun = () => {
    const validationError = validate();
    setLocalError(validationError);
    if (validationError || !objectType) return;

    if (mode === 'ids') {
      mutation.mutate({ mode: 'ids', request: { accountId, integrationKey, objectType, ids } });
      return;
    }
    const extraParams = extraParamsText.trim() ? JSON.parse(extraParamsText) : undefined;
    mutation.mutate({
      mode: 'dateRange',
      request: {
        accountId,
        integrationKey,
        objectType,
        fromDate: (fromDate as Date).toISOString(),
        toDate: (toDate as Date).toISOString(),
        dateField,
        maxItems,
        extraParams,
      },
    });
  };

  const result = mutation.data;

  return (
    <Stack spacing="md">
      <PlaygroundObjectSelect
        accountId={accountId}
        integrationKey={integrationKey}
        value={objectType}
        onChange={setObjectType}
      />

      {selectedObject && (
        <Group spacing="lg">
          <Text size="xs" color="dimmed">
            id field: <b>{selectedObject.idField ?? '—'}</b>
          </Text>
          <Text size="xs" color="dimmed">
            createdAt: <b>{selectedObject.createdAtField ?? '—'}</b>
          </Text>
          <Text size="xs" color="dimmed">
            updatedAt: <b>{selectedObject.updatedAtField ?? '—'}</b>
          </Text>
        </Group>
      )}

      <SegmentedControl
        value={mode}
        onChange={(value) => setMode(value as ItemsMode)}
        data={[
          { label: 'By date range', value: 'dateRange' },
          { label: 'By ids', value: 'ids' },
        ]}
      />

      {mode === 'ids' ? (
        <Textarea
          label={`Ids (comma or newline separated, max ${MAX_IDS})`}
          placeholder="123, 456, 789"
          autosize
          minRows={3}
          value={idsText}
          onChange={(event) => setIdsText(event.currentTarget.value)}
          description={ids.length > 0 ? `${ids.length} id(s)` : undefined}
        />
      ) : (
        <Stack spacing="sm">
          <Group grow align="flex-start">
            <DatePickerInput
              label="From date"
              placeholder="Pick a date"
              value={fromDate}
              onChange={setFromDate}
              maxDate={toDate ?? undefined}
              clearable
            />
            <DatePickerInput
              label="To date"
              placeholder="Pick a date"
              value={toDate}
              onChange={setToDate}
              minDate={fromDate ?? undefined}
              clearable
            />
          </Group>
          <Group grow align="flex-start">
            <Select
              label="Date field"
              value={dateField}
              onChange={(value) => setDateField((value as DateField) ?? 'updatedAt')}
              data={[
                { value: 'updatedAt', label: 'updatedAt' },
                { value: 'createdAt', label: 'createdAt' },
              ]}
            />
            <NumberInput
              label="Max items"
              value={maxItems}
              onChange={(value) => setMaxItems(typeof value === 'number' ? value : 50)}
              min={1}
              max={MAX_ITEMS}
            />
          </Group>
          <Textarea
            label="Extra params (advanced, JSON object)"
            description='For hierarchy integrations, e.g. { "channelId": "C123" }'
            placeholder="{}"
            autosize
            minRows={2}
            value={extraParamsText}
            onChange={(event) => setExtraParamsText(event.currentTarget.value)}
          />
        </Stack>
      )}

      {localError && (
        <Text size="sm" color="red">
          {localError}
        </Text>
      )}

      <Group>
        <Button
          leftIcon={<IconSearch size={16} />}
          color="bagel"
          onClick={handleRun}
          loading={mutation.isPending}
          disabled={!objectType}
        >
          Fetch items
        </Button>
      </Group>

      {mutation.isError && (
        <PlaygroundErrorDisplay error={extractPlaygroundError(mutation.error)} />
      )}

      {result && (
        <Stack spacing="xs">
          <Group spacing="xs">
            <Badge color="bagel" variant="light">
              {result.itemCount} item(s)
            </Badge>
            {result.truncated && (
              <Badge color="yellow" variant="light">
                truncated at maxItems
              </Badge>
            )}
            {result.timedOut && (
              <Badge color="orange" variant="light">
                timed out (partial)
              </Badge>
            )}
          </Group>
          <JsonBlock value={result.items} label="Items" />
        </Stack>
      )}
    </Stack>
  );
}
